/**
 * @fileoverview JobChips: the job pills on the Add / Edit to-do sheet (B140). The server sends the shared Jobs list for what the
 * to-do is about (the brief's todo.jobLists: plant, bed or garden, each job { code, label, color_key } in the reader's
 * language, in the owner's order); this draws them through the shared WordPills with the chosen job ringed. With no jobs it
 * draws nothing (the note shortcuts do not take its place). The app keeps no list of jobs and no words.
 *
 * Where: OwnTodoSheet.
 * API: none (the sheet reads the brief and passes the list).
 * Settings: none.
 * Words (en/es): todo.own.jobs (the row's name), todo.own.jobChosen (the ringed pill's aria-label); the pills' words are the server's.
 *
 * @module own-todos/JobChips
 */

import React from 'react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { WordPills } from '@/shared/components/WordPills';
import type { JobListItem } from '@/shared/api/todayBrief';

/** The job row's props. */
export interface JobChipsProps {
  /** The jobs for what the to-do is about, as the brief sends them. */
  jobs: JobListItem[];
  /** The chosen job's code, or null. */
  selected: string | null;
  /** A tap on a job. */
  onPick: (job: JobListItem) => void;
  /** Greys the pills while the to-do is being saved. */
  disabled?: boolean;
  /** Extra classes for the row. */
  className?: string;
}

/**
 * The job pills for one list type.
 *
 * @param props - JobChipsProps.
 */
export const JobChips: React.FC<JobChipsProps> = ({ jobs, selected, onPick, disabled = false, className }) => {
  const { reactTranslate: t } = useTranslation();
  const byCode = new Map(jobs.map((job) => [job.code, job]));
  return (
    <WordPills
      items={jobs.map((job) => ({ key: job.code, label: job.label, colorKey: job.color_key }))}
      selectedKey={selected}
      onPick={(item) => { const job = byCode.get(item.key); if (job) onPick(job); }}
      disabled={disabled}
      label={t('todo.own.jobs')}
      chosenLabel={(job) => t('todo.own.jobChosen', { job })}
      className={className}
    />
  );
};
